import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Space, message, Input, Select, Tabs, Form, Modal, InputNumber, Row, Col, Divider, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, HistoryOutlined, ShopOutlined } from '@ant-design/icons';
import api from '../services/api';

const { Option } = Select;

const TIPOS_MOV = { entrada: { cor: 'green', label: 'Entrada' }, saida: { cor: 'red', label: 'Saída' }, ajuste: { cor: 'blue', label: 'Ajuste' } };

export default function Estoque() {
  const [estoque, setEstoque] = useState([]);
  const [depositos, setDepositos] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [depositoSelecionado, setDepositoSelecionado] = useState(null);
  const [modalMovimentar, setModalMovimentar] = useState(false);
  const [modalDeposito, setModalDeposito] = useState(false);
  const [movItem, setMovItem] = useState(null);
  const [formMov] = Form.useForm();
  const [formDeposito] = Form.useForm();

  useEffect(() => { carregar(); }, []);

  const carregar = async () => {
    setLoading(true);
    try {
      const [rDep, rProd] = await Promise.all([
        api.get('/estoque/depositos'),
        api.get('/produtos'),
      ]);
      setDepositos(rDep.data);
      setProdutos(rProd.data);
      if (rDep.data.length > 0 && !depositoSelecionado) {
        setDepositoSelecionado(rDep.data[0].id);
        await carregarEstoque(rDep.data[0].id);
      }
    } catch { message.error('Erro ao carregar'); }
    finally { setLoading(false); }
  };

  const carregarEstoque = async (depId) => {
    const r = await api.get('/estoque' + (depId ? '?deposito_id=' + depId : ''));
    setEstoque(r.data);
  };

  const carregarHistorico = async () => {
    const r = await api.get('/estoque/historico' + (depositoSelecionado ? '?deposito_id=' + depositoSelecionado : ''));
    setHistorico(r.data);
  };

  const movimentar = async (values) => {
    try {
      await api.post('/estoque/movimentar', {
        produto_id: values.produto_id,
        deposito_id: values.deposito_id || depositoSelecionado,
        tipo: values.tipo,
        quantidade: values.quantidade,
        motivo: values.motivo,
      });
      message.success('Estoque atualizado!');
      setModalMovimentar(false);
      formMov.resetFields();
      carregarEstoque(depositoSelecionado);
    } catch(e) { message.error('Erro: ' + (e.response?.data?.erro || e.message)); }
  };

  const salvarDeposito = async (values) => {
    try {
      if (movItem) {
        await api.put('/estoque/depositos/' + movItem.id, values);
      } else {
        await api.post('/estoque/depositos', values);
      }
      message.success('Depósito salvo!');
      setModalDeposito(false);
      setMovItem(null);
      formDeposito.resetFields();
      carregar();
    } catch(e) { message.error('Erro: ' + (e.response?.data?.erro || e.message)); }
  };

  const colunasEstoque = [
    { title: 'Código', dataIndex: 'codigo', width: 90, render: v => <Tag color="blue">{v}</Tag> },
    { title: 'Produto', dataIndex: 'descricao', ellipsis: true },
    { title: 'TAG', dataIndex: 'tag', width: 100 },
    { title: 'Marca/Modelo', width: 150, render: (_, r) => [r.marca, r.modelo].filter(Boolean).join(' / ') || '-' },
    { title: 'Unidade', dataIndex: 'unidade', width: 80 },
    { title: 'Quantidade', dataIndex: 'quantidade', width: 110, render: v => <Tag color={parseFloat(v) > 0 ? 'green' : 'red'}>{parseFloat(v).toLocaleString('pt-BR')}</Tag> },
    { title: 'Ações', width: 120, render: (_, r) => (
      <Space size={4}>
        <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => { setMovItem(r); formMov.setFieldsValue({ produto_id: r.produto_id, deposito_id: depositoSelecionado, tipo: 'entrada' }); setModalMovimentar(true); }}>Movimentar</Button>
      </Space>
    )},
  ];

  const colunasHistorico = [
    { title: 'Data', dataIndex: 'criado_em', width: 140, render: v => v ? new Date(v).toLocaleString('pt-BR') : '-' },
    { title: 'Produto', dataIndex: 'descricao', ellipsis: true },
    { title: 'Tipo', dataIndex: 'tipo', width: 80, render: v => <Tag color={TIPOS_MOV[v]?.cor}>{TIPOS_MOV[v]?.label || v}</Tag> },
    { title: 'Quantidade', dataIndex: 'quantidade', width: 100, render: (v, r) => <span style={{ color: r.tipo === 'entrada' ? 'green' : 'red' }}>{r.tipo === 'entrada' ? '+' : '-'}{parseFloat(v).toLocaleString('pt-BR')}</span> },
    { title: 'Anterior', dataIndex: 'quantidade_anterior', width: 90, render: v => parseFloat(v||0).toLocaleString('pt-BR') },
    { title: 'Posterior', dataIndex: 'quantidade_posterior', width: 90, render: v => parseFloat(v||0).toLocaleString('pt-BR') },
    { title: 'Motivo', dataIndex: 'motivo', ellipsis: true },
    { title: 'Usuário', dataIndex: 'usuario_nome', width: 120 },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ margin: 0 }}>Estoque</h2>
          <p style={{ color: '#888', margin: 0 }}>Controle de entrada e saída de produtos</p>
        </div>
        <Space>
          <Select value={depositoSelecionado} onChange={v => { setDepositoSelecionado(v); carregarEstoque(v); }} style={{ width: 200 }}>
            {depositos.map(d => <Option key={d.id} value={d.id}>{d.nome}</Option>)}
          </Select>
          <Button icon={<EditOutlined />} onClick={() => { setMovItem(null); formDeposito.resetFields(); setModalDeposito(true); }}>Novo Depósito</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { setMovItem(null); formMov.resetFields(); formMov.setFieldsValue({ deposito_id: depositoSelecionado, tipo: 'entrada' }); setModalMovimentar(true); }}>Nova Movimentação</Button>
        </Space>
      </div>

      <Tabs onChange={key => { if (key === 'historico') carregarHistorico(); }} items={[
        {
          key: 'estoque',
          label: <span><ShopOutlined /> Estoque Atual</span>,
          children: (
            <Card size="small">
              <Table columns={colunasEstoque} dataSource={estoque} rowKey="id" size="small" loading={loading} pagination={{ pageSize: 20 }} />
            </Card>
          )
        },
        {
          key: 'historico',
          label: <span><HistoryOutlined /> Histórico</span>,
          children: (
            <Card size="small">
              <Table columns={colunasHistorico} dataSource={historico} rowKey="id" size="small" pagination={{ pageSize: 30 }} />
            </Card>
          )
        },
        {
          key: 'depositos',
          label: 'Depósitos',
          children: (
            <Card size="small">
              <Table dataSource={depositos} rowKey="id" size="small" pagination={false}
                columns={[
                  { title: 'Nome', dataIndex: 'nome' },
                  { title: 'Endereço', dataIndex: 'endereco', ellipsis: true },
                  { title: 'Cidade', dataIndex: 'cidade', width: 150 },
                  { title: 'Estado', dataIndex: 'estado', width: 70 },
                  { title: 'Ações', width: 80, render: (_, r) => (
                    <Button size="small" icon={<EditOutlined />} onClick={() => { setMovItem(r); formDeposito.setFieldsValue(r); setModalDeposito(true); }} />
                  )},
                ]}
              />
            </Card>
          )
        }
      ]} />

      <Modal title="Movimentação de Estoque" open={modalMovimentar} onCancel={() => { setModalMovimentar(false); formMov.resetFields(); }} footer={null}>
        <Form form={formMov} layout="vertical" onFinish={movimentar}>
          <Form.Item name="produto_id" label="Produto" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="children" placeholder="Selecione o produto">
              {produtos.map(p => <Option key={p.id} value={p.id}>{p.descricao} ({p.codigo})</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="deposito_id" label="Depósito" rules={[{ required: true }]}>
            <Select>{depositos.map(d => <Option key={d.id} value={d.id}>{d.nome}</Option>)}</Select>
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="tipo" label="Tipo" rules={[{ required: true }]}>
                <Select>
                  <Option value="entrada">Entrada</Option>
                  <Option value="saida">Saída</Option>
                  <Option value="ajuste">Ajuste</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="quantidade" label="Quantidade" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0} precision={3} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="motivo" label="Motivo">
            <Input placeholder="Ex: Compra NF 123, Venda, Ajuste de inventário..." />
          </Form.Item>
          <Button type="primary" htmlType="submit" block>Salvar Movimentação</Button>
        </Form>
      </Modal>

      <Modal title={movItem ? 'Editar Depósito' : 'Novo Depósito'} open={modalDeposito} onCancel={() => { setModalDeposito(false); setMovItem(null); formDeposito.resetFields(); }} footer={null}>
        <Form form={formDeposito} layout="vertical" onFinish={salvarDeposito}>
          <Form.Item name="nome" label="Nome" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="endereco" label="Endereço"><Input /></Form.Item>
          <Row gutter={16}>
            <Col span={16}><Form.Item name="cidade" label="Cidade"><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="estado" label="Estado"><Input maxLength={2} /></Form.Item></Col>
          </Row>
          <Button type="primary" htmlType="submit" block>Salvar</Button>
        </Form>
      </Modal>
    </div>
  );
}
